import { useEffect, useState } from 'react';
import { bookstoreApi, paymentsApi, type BookOrder, type BookPrice, type PaymentProvider } from '../lib/api';

const KIND_LABELS: Record<string, string> = {
  MODULE_THEORY: 'Livre théorique du module',
  MODULE_WORKBOOK: "Cahier d'exercices du module",
  CATALOGUE_BOOK: 'Ouvrage du catalogue',
};

function money(minor: number, currency: string) {
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

export function BookstorePage() {
  const [prices, setPrices] = useState<BookPrice[]>([]);
  const [orders, setOrders] = useState<BookOrder[]>([]);
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [itemKind, setItemKind] = useState('MODULE_THEORY');
  const [itemId, setItemId] = useState('');
  const [fulfilment, setFulfilment] = useState('DIGITAL');
  const [shipping, setShipping] = useState({
    buyer_first_name: '', buyer_last_name: '', country: '',
    address_line: '', city: '', postal_code: '', phone: '',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { reload(); }, []);

  async function reload() {
    try {
      const [p, o] = await Promise.all([bookstoreApi.prices(), bookstoreApi.myOrders()]);
      setPrices(p); setOrders(o);
    } catch (e: any) { setError(String(e.message || e)); }
    try { setProviders(await paymentsApi.providers()); } catch { /* aucun prestataire configuré */ }
  }

  const price = prices.find(p => p.item_kind === itemKind && p.fulfilment === fulfilment);
  // Une commande papier ne peut pas être expédiée sans adresse complète :
  // le serveur la refuse, autant l'indiquer avant l'envoi.
  const missing = fulfilment === 'PRINT'
    ? Object.entries(shipping).filter(([, v]) => !v.trim()).map(([k]) => k)
    : [];

  async function submit() {
    setError(''); setMessage(''); setBusy(true);
    try {
      const body: Record<string, unknown> = { item_kind: itemKind, item_id: itemId.trim(), fulfilment };
      if (fulfilment === 'PRINT') Object.assign(body, shipping);
      const order = await bookstoreApi.order(body);
      setMessage(
        order.kdp_queue_id
          ? `Commande enregistrée. Le texte est dans la passerelle de publication (${order.kdp_queue_id.slice(0, 8)}).`
          : 'Commande enregistrée.'
      );
      await reload();
    } catch (e: any) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">LIBRAIRIE</span>
        <h1>Ouvrages et cahiers d'exercices</h1>
        <p>Commandez le livre théorique ou le cahier d'exercices d'un module, en version numérique ou papier.</p>
      </div>
    </header>

    {error && <div className="alert error">{error}</div>}
    {message && <div className="notice">{message}</div>}

    <div className="grid two">
      <div className="card">
        <h3>Nouvelle commande</h3>
        <label>Type d'ouvrage
          <select value={itemKind} onChange={(e: any) => setItemKind(e.target.value)}>
            {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label>Identifiant du module ou de l'ouvrage
          <input value={itemId} onChange={(e: any) => setItemId(e.target.value)} placeholder="identifiant" />
        </label>
        <label>Format
          <select value={fulfilment} onChange={(e: any) => setFulfilment(e.target.value)}>
            <option value="DIGITAL">Numérique — lecture immédiate</option>
            <option value="PRINT">Papier — expédié à votre adresse</option>
          </select>
        </label>

        {price && <p className="success-note">Prix : <strong>{money(price.amount_minor, price.currency)}</strong></p>}

        {fulfilment === 'PRINT' && <div className="shipping-block">
          <h4>Adresse de livraison</h4>
          <p className="technical-note">Tous les champs sont nécessaires à l'expédition.</p>
          <label>Prénom<input value={shipping.buyer_first_name} onChange={(e: any) => setShipping({ ...shipping, buyer_first_name: e.target.value })} /></label>
          <label>Nom<input value={shipping.buyer_last_name} onChange={(e: any) => setShipping({ ...shipping, buyer_last_name: e.target.value })} /></label>
          <label>Pays<input value={shipping.country} onChange={(e: any) => setShipping({ ...shipping, country: e.target.value })} /></label>
          <label>Adresse<input value={shipping.address_line} onChange={(e: any) => setShipping({ ...shipping, address_line: e.target.value })} /></label>
          <label>Ville<input value={shipping.city} onChange={(e: any) => setShipping({ ...shipping, city: e.target.value })} /></label>
          <label>Code postal<input value={shipping.postal_code} onChange={(e: any) => setShipping({ ...shipping, postal_code: e.target.value })} /></label>
          <label>Téléphone<input value={shipping.phone} onChange={(e: any) => setShipping({ ...shipping, phone: e.target.value })} /></label>
          {missing.length > 0 && <p className="technical-note">Champs manquants : {missing.length}</p>}
        </div>}

        <button className="button primary" disabled={busy || !itemId.trim() || missing.length > 0} onClick={submit}>
          {busy ? 'Envoi…' : 'Commander'}
        </button>

        {providers.length === 0 && <p className="technical-note">Aucun moyen de paiement configuré pour ce tenant.</p>}
      </div>

      <div className="card">
        <h3>Tarifs en vigueur</h3>
        <table className="price-table">
          <thead><tr><th>Ouvrage</th><th>Format</th><th>Prix</th></tr></thead>
          <tbody>
            {prices.map(p => <tr key={`${p.item_kind}-${p.fulfilment}`}>
              <td>{KIND_LABELS[p.item_kind] || p.item_kind}</td>
              <td>{p.fulfilment === 'DIGITAL' ? 'Numérique' : 'Papier'}</td>
              <td>{money(p.amount_minor, p.currency)}</td>
            </tr>)}
            {!prices.length && <tr><td colSpan={3}>Tarifs non chargés.</td></tr>}
          </tbody>
        </table>

        <h3>Mes commandes</h3>
        {orders.map(o => <div key={o.id} className="order-row">
          <strong>{o.item_title || KIND_LABELS[o.item_kind] || o.item_kind}</strong>
          <span>{o.fulfilment === 'DIGITAL' ? 'Numérique' : 'Papier'} · {money(o.amount_minor, o.currency)}</span>
          <small>{o.status} · {new Date(o.created_at).toLocaleDateString()}</small>
        </div>)}
        {!orders.length && <p>Aucune commande.</p>}
      </div>
    </div>
  </section>;
}
